import React, { useEffect, useState } from 'react';

import {
    Button,
    Card,
    Form,
    Input,
    InputNumber,
    Modal,
    Space,
    Table,
    Tag,
    message
} from 'antd';

import {
    PlusOutlined,
    ReloadOutlined
} from '@ant-design/icons';

import {
    getKermesInventory,
    addKermesInventory,
    createKermesProduct
} from '../utils/api';

export default function KermesInventario({
    apiBase = '/api/kermes'
}) {

    const [inventory, setInventory] = useState([]);

    const [loading, setLoading] = useState(false);

    const [adding, setAdding] = useState({});

    const [amounts, setAmounts] = useState({});

    const [productModalOpen, setProductModalOpen] = useState(false);

    const [creatingProduct, setCreatingProduct] = useState(false);

    const [form] = Form.useForm();


    // =========================
    // CARGAR INVENTARIO
    // =========================

    const loadInventory = async () => {

        setLoading(true);

        try {

            const data = await getKermesInventory();

            setInventory(data.inventory || []);

        } catch (error) {

            message.error(error.message);

        } finally {

            setLoading(false);

        }
    };


    useEffect(() => {

        loadInventory();

    }, []);


    // =========================
    // AGREGAR INVENTARIO
    // =========================

    const handleAddInventory = async (productId) => {

        const amount = Number(
            amounts[productId] || 0
        );

        if (amount <= 0) {

            message.warning(
                'Ingresa una cantidad mayor a 0.'
            );

            return;
        }

        setAdding(prev => ({
            ...prev,
            [productId]: true
        }));

        try {

            const result = await addKermesInventory({
                product_id: productId,
                quantity: amount
            });

            if (!result.success) {

                throw new Error(
                    result.message ||
                    'No se pudo agregar el inventario.'
                );
            }

            message.success(
                `Se agregaron ${amount} piezas al inventario.`
            );

            // Limpiar input
            setAmounts(prev => ({
                ...prev,
                [productId]: 0
            }));

            // Recargar inventario
            await loadInventory();

        } catch (error) {

            message.error(error.message);

        } finally {

            setAdding(prev => ({
                ...prev,
                [productId]: false
            }));
        }
    };


    // =========================
    // ABRIR MODAL
    // =========================

    const handleOpenProductModal = () => {

        form.resetFields();

        form.setFieldsValue({
            initial_quantity: 0
        });

        setProductModalOpen(true);
    };


    // =========================
    // CREAR PRODUCTO
    // =========================

    const handleCreateProduct = async (values) => {

        const name = values.name?.trim();

        const price = Number(values.price || 0);

        const initialQuantity = Number(
            values.initial_quantity || 0
        );

        if (!name) {

            message.warning(
                'Ingresa el nombre del producto.'
            );

            return;
        }

        if (price <= 0) {

            message.warning(
                'El precio debe ser mayor a 0.'
            );

            return;
        }

        if (initialQuantity < 0) {

            message.warning(
                'La cantidad inicial no puede ser negativa.'
            );

            return;
        }

        setCreatingProduct(true);

        try {

            const result = await createKermesProduct({
                name,
                price,
                initial_quantity: initialQuantity
            });

            if (!result.success) {

                throw new Error(
                    result.message ||
                    'No se pudo crear el producto.'
                );
            }

            message.success(
                'Producto creado correctamente.'
            );

            setProductModalOpen(false);

            form.resetFields();

            await loadInventory();

        } catch (error) {

            message.error(error.message);

        } finally {

            setCreatingProduct(false);

        }
    };


    // =========================
    // COLUMNAS
    // =========================

    const columns = [

        {
            title: 'Producto',
            dataIndex: 'name'
        },

        {
            title: 'Precio',
            dataIndex: 'price',
            render: value =>
                `$${Number(value || 0).toFixed(2)}`
        },

        {
            title: 'Inicial',
            dataIndex: 'initial_quantity'
        },

        {
            title: 'Disponible',
            dataIndex: 'current_quantity',

            render: value => (

                <Tag
                    color={
                        value <= 20
                            ? 'red'
                            : 'green'
                    }
                >
                    {value}
                </Tag>

            )
        },

        {
            title: 'Agregar',
            key: 'add',

            render: (_, record) => (

                <Space>

                    <InputNumber
                        min={1}
                        placeholder="Cantidad"

                        value={
                            amounts[record.product_id] || null
                        }

                        onChange={value => {

                            setAmounts(prev => ({
                                ...prev,
                                [record.product_id]:
                                    value || 0
                            }));

                        }}
                    />

                    <Button
                        type="primary"
                        icon={<PlusOutlined />}

                        loading={
                            adding[record.product_id]
                        }

                        onClick={() =>
                            handleAddInventory(
                                record.product_id
                            )
                        }
                    >
                        Agregar
                    </Button>

                </Space>
            )
        }
    ];


    // =========================
    // RENDER
    // =========================

    return (

        <>

            <Card>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                        flexWrap: 'wrap',
                        marginBottom: 16
                    }}
                >
                    <span
                        style={{
                            fontSize: 16,
                            fontWeight: 600
                        }}
                    >
                        Inventario
                    </span>

                    <Space
                        wrap
                        size={[8, 8]}
                    >
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadInventory}
                            loading={loading}
                        >
                            Actualizar
                        </Button>

                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleOpenProductModal}
                        >
                            Nuevo producto
                        </Button>
                    </Space>
                </div>

                <Table
                    rowKey="product_id"
                    loading={loading}
                    dataSource={inventory}
                    columns={columns}
                    pagination={false}
                    scroll={{ x: 900 }}
                />
            </Card>

            {/* =========================
                MODAL NUEVO PRODUCTO
            ========================= */}

            <Modal
                title="Nuevo producto"
                open={productModalOpen}

                onCancel={() => {

                    if (!creatingProduct) {
                        setProductModalOpen(false);
                        form.resetFields();
                    }

                }}

                destroyOnClose

                okText="Crear producto"
                cancelText="Cancelar"

                confirmLoading={creatingProduct}

                onOk={() =>
                    form.submit()
                }
            >

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateProduct}
                >

                    <Form.Item
                        label="Nombre del producto"
                        name="name"

                        rules={[
                            {
                                required: true,
                                message:
                                    'Ingresa el nombre del producto.'
                            }
                        ]}
                    >

                        <Input
                            placeholder="Ej. Agua"
                            maxLength={100}
                        />

                    </Form.Item>


                    <Form.Item
                        label="Precio"
                        name="price"

                        rules={[
                            {
                                required: true,
                                message:
                                    'Ingresa el precio.'
                            },
                            {
                                type: 'number',
                                min: 0.01,
                                message:
                                    'El precio debe ser mayor a 0.'
                            }
                        ]}
                    >

                        <InputNumber
                            prefix="$"
                            min={0.01}
                            precision={2}
                            style={{
                                width: '100%'
                            }}

                            placeholder="0.00"
                        />

                    </Form.Item>


                    <Form.Item
                        label="Cantidad inicial"
                        name="initial_quantity"

                        rules={[
                            {
                                required: true,
                                message:
                                    'Ingresa la cantidad inicial.'
                            },
                            {
                                type: 'number',
                                min: 0,
                                message:
                                    'La cantidad no puede ser negativa.'
                            }
                        ]}
                    >

                        <InputNumber
                            min={0}
                            precision={0}
                            style={{
                                width: '100%'
                            }}

                            placeholder="0"
                        />

                    </Form.Item>

                </Form>

            </Modal>

        </>

    );
}